import React, { createContext, useContext, useEffect, useMemo } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { extractDomain } from "@/utils/emailUtils";

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  session: Session | null;
  user: (User & Partial<Profile>) | null;
  isAdmin: boolean;
  loading: boolean;
  hasHubspotSupport: boolean;
  userEmailDomain: string | null;
  userRoles: string[]; 
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  isAdmin: false,
  loading: true,
  hasHubspotSupport: false,
  userEmailDomain: null,
  userRoles: [], 
  logout: async () => {},
  refreshSession: async () => {},
});

// Function to fetch session data with a timeout
const fetchSession = async () => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000); // 10-second timeout

  try {
    const { data, error } = await supabase.auth.getSession();
    clearTimeout(timeout);
    
    if (error) {
      console.error("Error fetching session:", error);
      throw error;
    }
    
    return data.session;
  } catch (error) {
    clearTimeout(timeout);
    console.error("Error fetching session:", error);
    return null; // Return null instead of throwing to prevent React Query retries
  }
};

// Function to update the user's last login timestamp
const updateLastLoginTimestamp = async (userId: string) => {
  try {
    console.log("Auth context: Updating last login timestamp for user:", userId);
    
    const { error } = await supabase
      .from('profiles')
      .update({ last_login: new Date().toISOString() })
      .eq('id', userId);
    
    if (error) {
      console.error("Auth context: Error updating last login timestamp:", error);
    } else {
      console.log("Auth context: Last login timestamp updated successfully");
    }
  } catch (err) {
    console.error("Auth context: Failed to update last login timestamp:", err);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch session using React Query with improved error handling
  const { data: session, isLoading, refetch: refetchSession } = useQuery({
    queryKey: ['session'],
    queryFn: fetchSession,
    retry: 1, // Only retry once to reduce repeated failures
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    meta: {
      errorHandler: (error: any) => {
        console.error("Session fetch error in React Query:", error);
        // Don't redirect on error - let the app handle session state naturally
      }
    }
  });
  
  // Function to refresh the session with improved handling
  const refreshSession = async () => {
    try {
      console.log("Attempting to refresh session...");
      // Try to refresh the session with Supabase
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error("Error refreshing session:", error);
        // Clear session if refresh failed due to invalid refresh token
        if (error.message?.includes("invalid refresh token")) {
          queryClient.setQueryData(['session'], null);
        }
        return;
      }
      
      // Update the session in React Query cache
      if (data.session) {
        console.log("Session refreshed successfully");
        queryClient.setQueryData(['session'], data.session);
        
        // Refetch profile data when session is refreshed
        if (data.session.user?.id) {
          queryClient.invalidateQueries({ queryKey: ['profile', data.session.user.id] });
        }
      } else {
        console.log("No session returned after refresh attempt");
      }
    } catch (err) {
      console.error("Failed to refresh session:", err);
    }
  };
  
  // Setup auth state change listener
  useEffect(() => {
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log("Auth state change:", event);
        
        if (event === 'SIGNED_IN') {
          console.log("User signed in, updating session");
          // Update the session in React Query cache
          queryClient.setQueryData(['session'], newSession);
          
          // Update last login timestamp when user signs in
          if (newSession?.user?.id) {
            // Defer timestamp update to avoid blocking auth flow
            setTimeout(() => {
              updateLastLoginTimestamp(newSession.user.id);
            }, 0);
          }
          
          // Regenerate session ID on login for enhanced security
          // We use setTimeout to avoid deadlocks in Supabase auth
          setTimeout(() => {
            refreshSession();
          }, 0);
          
          // Refetch profile data when session changes
          if (newSession?.user?.id) {
            queryClient.invalidateQueries({ queryKey: ['profile', newSession.user.id] });
            queryClient.invalidateQueries({ queryKey: ['isAdmin', newSession.user.id] });
          }
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out, clearing session");
          // Clear session data
          queryClient.setQueryData(['session'], null);
          queryClient.clear(); // Clear all queries on signout
        } else if (event === 'TOKEN_REFRESHED') {
          console.log("Token refreshed, updating session");
          // Update the session in React Query cache when token is refreshed
          queryClient.setQueryData(['session'], newSession);
        } else if (event === 'USER_UPDATED') {
          console.log("User updated, updating session");
          queryClient.setQueryData(['session'], newSession);
          
          if (newSession?.user?.id) {
            queryClient.invalidateQueries({ queryKey: ['profile', newSession.user.id] });
          }
        }
      }
    );

    // Check for existing session on mount
    const checkExistingSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Error checking existing session:", error);
          return;
        }
        
        if (data.session) {
          console.log("Existing session found on app mount");
          queryClient.setQueryData(['session'], data.session);
        }
      } catch (err) {
        console.error("Failed to check existing session:", err);
      }
    };
    
    checkExistingSession();

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);
  
  // Query for user profile data using the session user ID
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!session?.user?.id, // Only run this query if we have a user ID
  });
  
  // Admin status query
  const { data: isAdmin = false, isLoading: adminLoading } = useQuery({
    queryKey: ['isAdmin', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return false;
      
      const { data, error } = await supabase.rpc('is_admin', {
        user_id: session.user.id
      });
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return data;
    },
    enabled: !!session?.user?.id, // Only run if we have a user ID
  });
  
  // Combine session user with profile data
  const user = session?.user ? {
    ...session.user,
    ...(profileData || {}),
  } : null;
  
  // Get user email domain for ticket filtering
  const userEmailDomain = user?.email ? extractDomain(user.email) : null;
  
  // Check if user has Hubspot support role
  const hasHubspotSupport = !!profileData && 
    (profileData.role === 'Soporte Hubspot' || 
    (profileData.access || []).includes('Soporte Hubspot (MKT/Sales Ops)'));
  
  // Extract user roles from profile data
  const userRoles = profileData?.role ? [profileData.role] : [];
  
  // Update HubSpot user identification
  React.useEffect(() => {
    if (user?.email && window.hsConversationsOnReady) {
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      
      window.hsConversationsOnReady.push(() => {
        console.log('HubSpot widget ready, attempting to identify user');
        try {
          window.HubSpotConversations?.widget?.identify(
            user.email,
            {
              email: user.email,
              hubspot_portal_id: profileData?.hubspot_portal_id || undefined,
              organization: profileData?.organization_name || undefined
            }
          );
          console.log('Successfully identified user in HubSpot');
        } catch (error) {
          console.error('Error identifying HubSpot user:', error);
        }
      });
    }
  }, [user?.email, profileData]);

  // Enhanced Logout function with proper cookie and session cleanup
  const logout = async () => {
    try {
      console.log("Logging out user...");
      
      // Properly sign out through Supabase auth
      const { error } = await supabase.auth.signOut({
        scope: 'local' // Only clear local session, not other browser tabs
      });
      
      if (error) {
        console.error('Error during logout:', error);
        throw error;
      }
      
      // Clear all react-query cache
      queryClient.clear();
      
      // Redirect to auth page after successful logout
      navigate('/auth', { replace: true });
      
      console.log("Logout successful");
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Fallback: force redirect to auth page even if logout failed
      window.location.href = '/auth';
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        session, 
        user, 
        isAdmin, 
        loading: isLoading || profileLoading || adminLoading, 
        hasHubspotSupport,
        userEmailDomain,
        userRoles,
        logout,
        refreshSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
